/**
 * MentalHealthPanel
 * ──────────────────────────────────────────────────────────────────
 * Replaces the standard SOAPPanel for Psychiatry / Mental Health.
 * All data below is surface-level mock — the structure exists so
 * the real LLM + EHR backends can plug into the same keys.
 *
 *   <MentalHealthPanel patient={...} activeTab={...} setActiveTab={...} />
 *
 * Sections:
 *   1. 42 CFR Part 2 banner + confidentiality toggle
 *   2. Session info (date · duration · modality · CPT add-ons)
 *   3. Mental Status Exam (structured fields)
 *   4. Risk Assessment (auto-expanded when SI keyword mentioned in transcript)
 *   5. Outcome scales — PHQ-9, GAD-7, C-SSRS w/ sparkline trends
 *   6. Assessment & Formulation (free-text)
 *   7. Sidebar: Treatment Plan Progress
 */
import { useState, useMemo } from 'react';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../ui/tabs';
import {
  Copy, Shield, Brain, AlertTriangle, ActivitySquare, Heart, Target, FileText,
  TrendingUp, TrendingDown, Minus, ShieldAlert, CheckCircle2, Clock, UserCheck,
} from 'lucide-react';
import { toast } from 'sonner';

/* ── Mock MSE field defaults ── */
const MSE_TEMPLATE = {
  appearance:     'Well-groomed, appropriately dressed, no psychomotor agitation.',
  behavior:       'Cooperative, good eye contact, no abnormal movements.',
  speech:         'Normal rate, rhythm, volume; spontaneous and fluent.',
  mood:           'Patient reports feeling "down more days than not" this week.',
  affect:         'Constricted, congruent with reported mood.',
  thought_process: 'Linear, goal-directed, no loosening of associations.',
  thought_content: 'Denies current SI/HI/AH/VH; denies delusions or paranoia.',
  cognition:      'Alert and oriented ×3; attention and memory intact.',
  insight:        'Fair.',
  judgment:       'Fair; engaged in treatment planning.',
};

const SCALES = [
  { id: 'phq9', label: 'PHQ-9', range: '0–27', now: 14, prev: [18, 17, 16, 15, 14], interp: 'Moderate depression', tone: 'amber' },
  { id: 'gad7', label: 'GAD-7', range: '0–21', now: 9,  prev: [12, 11, 10, 10, 9],  interp: 'Mild anxiety', tone: 'green' },
  { id: 'cssrs', label: 'C-SSRS', range: '0–5', now: 1, prev: [2, 1, 1, 1, 1],      interp: 'Passive ideation only', tone: 'amber' },
];

const TREATMENT_PLAN = [
  { goal: 'Reduce PHQ-9 to <10',        target: '8 weeks', status: 'on-track', progress: 55 },
  { goal: 'Daily mood log adherence',   target: '6 weeks', status: 'at-risk',  progress: 35 },
  { goal: 'Reinstate social activity',  target: '4 weeks', status: 'complete', progress: 100 },
  { goal: 'CBT skills — cognitive reframing', target: '12 weeks', status: 'on-track', progress: 40 },
];

const Sparkline = ({ series = [], tone = 'slate' }) => {
  const max = Math.max(...series, 1);
  const pts = series.map((v, i) => `${(i / (series.length - 1 || 1)) * 100},${100 - (v / max) * 100}`).join(' ');
  const color = tone === 'amber' ? '#b45309' : tone === 'green' ? '#047857' : '#334155';
  const last = series[series.length - 1];
  const prev = series[series.length - 2];
  const TrendIcon = last < prev ? TrendingDown : last > prev ? TrendingUp : Minus;
  return (
    <div className="flex items-center gap-1.5">
      <svg viewBox="0 0 100 100" className="h-5 w-14">
        <polyline fill="none" stroke={color} strokeWidth="4" points={pts} />
      </svg>
      <TrendIcon className="h-3 w-3" style={{ color }} />
    </div>
  );
};

const ToneChip = ({ tone, children }) => {
  const cls = tone === 'amber' ? 'bg-amber-50 text-amber-800 border-amber-200'
            : tone === 'red'   ? 'bg-red-50 text-red-700 border-red-200'
            : tone === 'green' ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
            : 'bg-slate-100 text-slate-700 border-slate-200';
  return <Badge className={`text-[9px] border ${cls}`}>{children}</Badge>;
};

export const MentalHealthPanel = ({
  patient, activeTab: _activeTab, setActiveTab: _setActiveTab, transcript,
  onDone, requestCopy,
}) => {
  const [mse, setMse] = useState(MSE_TEMPLATE);
  const [riskOpen, setRiskOpen] = useState(false);
  const [cfr42, setCfr42] = useState(true);
  /* MH panel owns its own tab state — parent's activeTab uses SOAP
     vocabulary ('soap'/'transcript'/'billing') which doesn't map cleanly. */
  const [tab, setTab] = useState('note');
  const [assessment, setAssessment] = useState(
    'MDD, recurrent, moderate (F33.1) with partial response to sertraline 100 mg. GAD features improving. ' +
    'Patient engaged in weekly CBT. No current SI/HI. Continues safety plan and supportive social network.'
  );
  const [plan, setPlan] = useState(
    '1. Continue sertraline 100 mg daily; reassess in 2 weeks.\n' +
    '2. Continue weekly CBT — focus on cognitive reframing & behavioral activation.\n' +
    '3. Re-administer PHQ-9 and GAD-7 at next visit.\n' +
    '4. Reinforce safety plan; crisis resources reviewed.\n' +
    '5. Return in 2 weeks; sooner for any worsening SI or functional decline.'
  );

  /* Auto-surface risk section if transcript mentions any SI keyword. */
  const siDetected = useMemo(() => {
    const raw = transcript;
    const t = typeof raw === 'string'
      ? raw.toLowerCase()
      : Array.isArray(raw)
        ? raw.map((x) => typeof x === 'string' ? x : x?.text || '').join(' ').toLowerCase()
        : '';
    return /\b(suicide|suicidal|self.harm|kill myself|end it|hopeless)\b/.test(t);
  }, [transcript]);

  const copyNote = () => {
    const text =
`PSYCHIATRY NOTE — ${patient?.firstName || ''} ${patient?.lastName || ''}
42 CFR Part 2: ${cfr42 ? 'Protected' : 'Standard HIPAA only'}

SESSION
- Date: ${new Date().toLocaleDateString()}
- Duration: 45 min (CPT 90836 add-on)
- Modality: Individual psychotherapy + med management

MENTAL STATUS EXAM
${Object.entries(mse).map(([k, v]) => `- ${k.replace(/_/g, ' ')}: ${v}`).join('\n')}

OUTCOME SCALES
${SCALES.map((s) => `- ${s.label}: ${s.now} (${s.interp})`).join('\n')}

ASSESSMENT / FORMULATION
${assessment}

PLAN
${plan}`;
    if (requestCopy) requestCopy(text, 'Mental Health Note');
    else { navigator.clipboard.writeText(text); toast.success('Note copied'); }
  };

  return (
    <div className="space-y-3" data-testid="mental-health-panel">
      {/* 42 CFR Part 2 banner */}
      <div
        className={`rounded-xl border p-3 flex items-center gap-3 ${cfr42 ? 'border-indigo-200 bg-indigo-50/40' : 'border-slate-200 bg-slate-50'}`}
        data-testid="mh-42cfr-banner"
      >
        <Shield className={`h-4 w-4 ${cfr42 ? 'text-indigo-700' : 'text-slate-500'}`} />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold text-slate-900">
            {cfr42 ? '42 CFR Part 2 Protected' : 'HIPAA standard (not enhanced)'}
          </p>
          <p className="text-[10px] text-slate-500">
            {cfr42
              ? 'SUD + behavioral-health specific disclosures require explicit patient consent.'
              : 'Toggle on if the patient has any substance-use records in this chart.'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCfr42((p) => !p)}
          className={`h-6 w-10 rounded-full relative transition ${cfr42 ? 'bg-indigo-900' : 'bg-slate-300'}`}
          data-testid="mh-42cfr-toggle"
        >
          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition ${cfr42 ? 'left-5' : 'left-0.5'}`} />
        </button>
      </div>

      {/* Top action bar — mirrors SOAPPanel visual weight */}
      <div className="bg-slate-900 rounded-xl p-3" data-testid="mh-action-bar">
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={copyNote} className="bg-white text-slate-900 hover:bg-slate-100 gap-1.5 font-semibold h-8 text-xs" data-testid="mh-copy-full">
            <Copy className="h-3 w-3" /> Copy Full Note
          </Button>
          <Button size="sm" variant="ghost" className="text-white hover:bg-white/10 h-8 text-xs gap-1.5" onClick={() => toast.info('Treatment plan exported')}>
            <Target className="h-3 w-3" /> Export Tx Plan
          </Button>
          <div className="flex-1" />
          <Badge className="bg-white/15 text-white border-0 text-[10px] gap-1">
            <Brain className="h-2.5 w-2.5" /> Mental Health Mode
          </Badge>
          <Button size="sm" variant="ghost" className="text-xs text-slate-300 hover:bg-white/10 h-7" onClick={onDone} data-testid="mh-done-btn">Done</Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 h-8">
          <TabsTrigger value="note"       className="text-xs h-6 px-3" data-testid="mh-tab-note">Note</TabsTrigger>
          <TabsTrigger value="transcript" className="text-xs h-6 px-3" data-testid="mh-tab-transcript">Transcript</TabsTrigger>
          <TabsTrigger value="plan"       className="text-xs h-6 px-3" data-testid="mh-tab-plan">Treatment Plan</TabsTrigger>
        </TabsList>

        {/* Main note tab — full body */}
        <TabsContent value="note" className="mt-3 space-y-3">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {/* LEFT 2/3 */}
            <div className="lg:col-span-2 space-y-3">
              {/* Session info */}
              <section className="rounded-xl border border-slate-200 bg-white p-4" data-testid="mh-session-info">
                <header className="flex items-center gap-2 mb-3">
                  <Clock className="h-3.5 w-3.5 text-slate-500" />
                  <p className="text-xs font-bold text-slate-800">Session</p>
                  <Badge className="ml-auto text-[9px] bg-slate-100 text-slate-600 border-0">Today · 45 min</Badge>
                </header>
                <div className="grid grid-cols-3 gap-3 text-[11px]">
                  <div>
                    <p className="text-[9px] uppercase tracking-wide text-slate-400">Modality</p>
                    <p className="font-medium text-slate-800 mt-0.5">Individual · In-person</p>
                  </div>
                  <div>
                    <p className="text-[9px] uppercase tracking-wide text-slate-400">Add-on codes</p>
                    <p className="font-medium text-slate-800 mt-0.5">90836 (45 min psychotherapy)</p>
                  </div>
                  <div>
                    <p className="text-[9px] uppercase tracking-wide text-slate-400">Diagnosis (primary)</p>
                    <p className="font-medium text-slate-800 mt-0.5">F33.1 · MDD recurrent, moderate</p>
                  </div>
                </div>
              </section>

              {/* Mental Status Exam */}
              <section className="rounded-xl border border-slate-200 bg-white p-4" data-testid="mh-mse">
                <header className="flex items-center gap-2 mb-3">
                  <ActivitySquare className="h-3.5 w-3.5 text-slate-500" />
                  <p className="text-xs font-bold text-slate-800">Mental Status Exam</p>
                  <Badge className="ml-auto text-[9px] bg-indigo-50 text-indigo-700 border-indigo-200">Auto-populated</Badge>
                </header>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(mse).map(([k, v]) => (
                    <div key={k} className="space-y-0.5">
                      <p className="text-[9px] uppercase tracking-wide text-slate-400">{k.replace(/_/g, ' ')}</p>
                      <textarea
                        value={v}
                        onChange={(e) => setMse({ ...mse, [k]: e.target.value })}
                        className="w-full text-[11px] px-2 py-1.5 rounded border border-slate-200 bg-slate-50/60 resize-none focus:border-slate-400 focus:bg-white outline-none"
                        rows={2}
                        data-testid={`mh-mse-${k}`}
                      />
                    </div>
                  ))}
                </div>
              </section>

              {/* Risk Assessment */}
              <section
                className={`rounded-xl border p-4 ${siDetected || riskOpen ? 'border-red-200 bg-red-50/40' : 'border-slate-200 bg-white'}`}
                data-testid="mh-risk"
              >
                <header className="flex items-center gap-2">
                  {siDetected ? <ShieldAlert className="h-4 w-4 text-red-600" /> : <Shield className="h-3.5 w-3.5 text-slate-500" />}
                  <p className="text-xs font-bold text-slate-800">Risk Assessment</p>
                  {siDetected && <Badge className="text-[9px] bg-red-100 text-red-800 border border-red-300">SI mention detected</Badge>}
                  <button
                    type="button"
                    onClick={() => setRiskOpen((p) => !p)}
                    className="ml-auto text-[10px] font-semibold text-slate-600 hover:text-slate-900"
                    data-testid="mh-risk-toggle"
                  >
                    {riskOpen || siDetected ? 'Collapse' : 'Expand'}
                  </button>
                </header>
                {(siDetected || riskOpen) && (
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    {[
                      ['Suicidal ideation',  'Passive, fleeting; denies plan/intent/means.'],
                      ['Homicidal ideation', 'Denies.'],
                      ['Self-harm',          'No recent; history of cutting 2 years ago (in remission).'],
                      ['Access to means',    'No firearms; partner holds medications.'],
                      ['Protective factors', 'Strong therapeutic alliance; supportive partner; future-oriented.'],
                      ['Safety plan',        'Reviewed and updated today; copy given to patient.'],
                    ].map(([k, v]) => (
                      <div key={k} className="space-y-0.5">
                        <p className="text-[9px] uppercase tracking-wide text-slate-400">{k}</p>
                        <p className="text-[11px] text-slate-700">{v}</p>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Outcome scales */}
              <section className="rounded-xl border border-slate-200 bg-white p-4" data-testid="mh-scales">
                <header className="flex items-center gap-2 mb-3">
                  <Heart className="h-3.5 w-3.5 text-slate-500" />
                  <p className="text-xs font-bold text-slate-800">Outcome scales</p>
                  <Badge className="ml-auto text-[9px] bg-slate-100 text-slate-600 border-0">Last 5 sessions</Badge>
                </header>
                <div className="grid grid-cols-3 gap-3">
                  {SCALES.map((s) => (
                    <div key={s.id} className="rounded-lg border border-slate-200 p-3" data-testid={`mh-scale-${s.id}`}>
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] font-semibold text-slate-800">{s.label}</p>
                        <ToneChip tone={s.tone}>{s.interp}</ToneChip>
                      </div>
                      <p className="mt-1 text-lg font-bold tabular-nums text-slate-900">{s.now}<span className="text-[10px] text-slate-400 font-normal"> / {s.range.split('–')[1]}</span></p>
                      <Sparkline series={s.prev} tone={s.tone} />
                    </div>
                  ))}
                </div>
              </section>

              {/* Assessment & Plan */}
              <section className="rounded-xl border border-slate-200 bg-white p-4" data-testid="mh-ap">
                <header className="flex items-center gap-2 mb-3">
                  <FileText className="h-3.5 w-3.5 text-slate-500" />
                  <p className="text-xs font-bold text-slate-800">Assessment & Formulation</p>
                </header>
                <textarea
                  value={assessment}
                  onChange={(e) => setAssessment(e.target.value)}
                  className="w-full text-[11px] px-3 py-2 rounded-lg border border-slate-200 bg-slate-50/60 focus:bg-white focus:border-slate-400 outline-none"
                  rows={4}
                  data-testid="mh-assessment"
                />
                <p className="mt-3 text-xs font-bold text-slate-800">Plan</p>
                <textarea
                  value={plan}
                  onChange={(e) => setPlan(e.target.value)}
                  className="mt-1 w-full text-[11px] px-3 py-2 rounded-lg border border-slate-200 bg-slate-50/60 focus:bg-white focus:border-slate-400 outline-none font-mono"
                  rows={6}
                  data-testid="mh-plan"
                />
              </section>
            </div>

            {/* RIGHT 1/3 — Treatment Plan Progress */}
            <aside className="space-y-3">
              <section className="rounded-xl border border-slate-200 bg-white p-4" data-testid="mh-treatment-plan">
                <header className="flex items-center gap-2 mb-3">
                  <Target className="h-3.5 w-3.5 text-slate-500" />
                  <p className="text-xs font-bold text-slate-800">Treatment Plan Progress</p>
                </header>
                <div className="space-y-3">
                  {TREATMENT_PLAN.map((g, i) => {
                    const barColor = g.status === 'complete' ? 'bg-emerald-500' : g.status === 'at-risk' ? 'bg-amber-500' : 'bg-indigo-500';
                    const Icon = g.status === 'complete' ? CheckCircle2 : g.status === 'at-risk' ? AlertTriangle : UserCheck;
                    return (
                      <div key={i} className="space-y-1" data-testid={`mh-tx-goal-${i}`}>
                        <div className="flex items-start gap-2">
                          <Icon className={`h-3 w-3 mt-0.5 shrink-0 ${g.status === 'complete' ? 'text-emerald-600' : g.status === 'at-risk' ? 'text-amber-600' : 'text-indigo-600'}`} />
                          <p className="text-[11px] font-medium text-slate-800 flex-1">{g.goal}</p>
                          <span className="text-[9px] text-slate-400">{g.target}</span>
                        </div>
                        <div className="h-1 rounded-full bg-slate-100 overflow-hidden ml-5">
                          <div className={`h-full ${barColor}`} style={{ width: `${g.progress}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={() => toast.success('Added new goal')}
                  className="mt-3 w-full text-[10px] font-semibold text-slate-600 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 py-1.5 rounded-md border border-dashed border-slate-300"
                  data-testid="mh-add-goal"
                >
                  + Add goal
                </button>
              </section>

              <section className="rounded-xl border border-slate-200 bg-white p-4">
                <header className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                  <p className="text-xs font-bold text-slate-800">Care-gap alerts</p>
                </header>
                <ul className="space-y-1.5 text-[11px] text-slate-700">
                  <li>• Metabolic monitoring due — on SGA &gt; 3 months.</li>
                  <li>• PHQ-9 re-administration scheduled for next visit.</li>
                </ul>
              </section>
            </aside>
          </div>
        </TabsContent>

        <TabsContent value="transcript" className="mt-3">
          <div className="rounded-xl border border-slate-200 bg-white p-4 text-[11px] text-slate-700 whitespace-pre-wrap" data-testid="mh-transcript">
            {typeof transcript === 'string'
              ? (transcript || 'No transcript captured for this session yet.')
              : Array.isArray(transcript)
                ? transcript.map((x, i) => (
                    <div key={i} className="mb-1">{typeof x === 'string' ? x : `${x?.speaker ? x.speaker + ': ' : ''}${x?.text || ''}`}</div>
                  ))
                : 'No transcript captured for this session yet.'}
          </div>
        </TabsContent>

        <TabsContent value="plan" className="mt-3">
          <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="mh-plan-tab">
            <p className="text-xs font-bold text-slate-800 mb-2">Longitudinal Treatment Plan</p>
            <p className="text-[11px] text-slate-600">
              Rolling goals, adherence, and outcome-scale trends aggregated across the past 12 sessions.
              (Full visualization coming in the next iteration.)
            </p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MentalHealthPanel;
